import { NestedBubbleMetadata } from '@/components/public/Visualisations/NestedBubbles'
import { Shapes } from '@/components/public/Visualisations/types'
import Konva from 'konva'
import _clamp from 'lodash/clamp'

const MIN_FONT_SIZE_BY_DISPLAY_LEVEL: Record<number, number> = {
  2: 10,
  3: 8,
}
const MIN_FONT_SIZE = 6
const MAX_FONT_SIZE = 29

interface HeaderCacheProps {
  fontSize: number
  text: string
  data: string
}
export class NestedBubbleHeader extends Konva.TextPath implements Shapes.Shape {
  static readonly shapeName = 'NestedBubbleHeader'
  private _metadata: NestedBubbleMetadata
  private _maxDisplayLevelPropsCache: Record<number, HeaderCacheProps> = {}

  className = NestedBubbleHeader.shapeName
  capabilities: Shapes.Capability[] = []

  constructor(metadata: NestedBubbleMetadata) {
    super({
      fill: metadata.fontColour,
      fontStyle: 'bold',
      textBaseline: 'top',
      listening: false,
    })

    this._metadata = metadata
    this.fontSize(this._fontSize)
    this.text(this._text)
    this.data(this.createArcPath())
  }

  get key() {
    return this.path.toString()
  }

  get path() {
    return this._metadata.path
  }

  get center() {
    return this.position()
  }

  refresh() {
    this.fill(this._metadata.fontColour)

    const cachedValues = this._maxDisplayLevelPropsCache[this._metadata.maxDisplayLevel]
    const currentFontSize = this.fontSize()

    if (!cachedValues) {
      const newFontSize = this._fontSize
      if (currentFontSize !== newFontSize) {
        this.fontSize(newFontSize)
        this.text(this._text)
        this.data(this.createArcPath())
      }
      this._maxDisplayLevelPropsCache[this._metadata.maxDisplayLevel] = {
        fontSize: this.fontSize(),
        text: this.text(),
        data: this.data(),
      }
    } else if (currentFontSize !== cachedValues.fontSize) {
      this.fontSize(cachedValues.fontSize)
      this.text(cachedValues.text)
      this.data(cachedValues.data)
    }
  }

  private get _fontSize() {
    return _clamp(
      this._metadata.radius / 4,
      MIN_FONT_SIZE_BY_DISPLAY_LEVEL[this._metadata.maxDisplayLevel] ?? MIN_FONT_SIZE,
      MAX_FONT_SIZE / this._metadata.userZoomScale,
    )
  }

  private createArcPath() {
    const x = this._metadata.position.x
    const y = this._metadata.position.y
    const radius = this._metadata.radius
    const textWidth = this.textWidth + this.text().length / 2

    // Calculate the angular length of the text
    const textAngularLength = textWidth / radius

    // Adjust start and end angles to center the text
    const startAngle = -Math.PI / 2 - textAngularLength / 2
    const endAngle = -Math.PI / 2 + textAngularLength / 2

    // Create an arc path
    return `M ${x + radius * Math.cos(startAngle)} ${y + radius * Math.sin(startAngle)}
                  A ${radius} ${radius} 0 0 1 ${x + radius * Math.cos(endAngle)} ${
                    y + radius * Math.sin(endAngle)
                  }`
  }

  private get _text() {
    const textShape = new Konva.Text({
      text: '...',
      fontSize: this._fontSize,
    })
    // maxWidth = half the circumference taking into account the height of the text
    const maxWidth = Math.PI * this._metadata.radius - textShape.textHeight / 2
    const ellipsisWidth = this.textShapeWidth(textShape)

    let label = this._metadata.label
    let result = label
    textShape.text(result)
    let textWidth = this.textShapeWidth(textShape)

    while (textWidth > maxWidth) {
      if (label.length <= 1) {
        break // Break if the label is too short to trim further
      }

      // Calculate the number of characters to remove,
      // we need to include the addition of the ellipsis in this calculation as it
      // will be added to the label in the next step
      const approxCharWidth = Math.floor(textWidth / result.length)
      const charsToRemove = Math.ceil((textWidth + ellipsisWidth - maxWidth) / approxCharWidth)

      // Trim the label and add an ellipsis to the result
      label = label.substring(0, label.length - charsToRemove).trim()
      result = `${label}...`
      textShape.text(result)
      textWidth = this.textShapeWidth(textShape)
    }

    return result
  }

  private textShapeWidth(textShape: Konva.Text) {
    return textShape.textWidth + textShape.text().length / 2
  }
}
